import Dialog from 'components/Dialog';
import React from 'react';
import {Grid} from '@mui/material';
import FormikTextField from 'components/inputs/FormikTextField';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import * as yup from 'common/yup';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {useDispatch, useSelector} from 'react-redux';
import {layoutActions} from 'features/layout/redux';
import {packId, unpackId} from 'common/diplomatIdUtil';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import {Trans, useTranslation} from 'react-i18next';
import selectors from 'features/layout/redux/selectors';
import {IDiplomatObject} from 'types/DiplomatObject';
import history from 'redux/history';
import {IPartner} from 'types/Partner';
import {partnersActions} from 'redux/partners';
import Formik from 'components/hook-form/Formik';

const validationSchema = yup.object({
   partnerName: yup.string().required(),
});

type Props = {
   data?: INav;
   onClose: () => void;
   partner?: IPartner;
};

const SaveAsPartnerDialog = ({data, onClose, partner}: Props) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const {publicPartners, trustedPartners} = useSelector(selectors.getPartners);
   const partnersForValidation = React.useRef<IDiplomatObject[]>(publicPartners);

   const onSuccess = (data: IPartner) => {
      dispatch(layoutActions.loadTreeNav());
      onClose();
      history.push(`/${packId(data.objectType, data.id)}`);
   };
   const {id, objectType} = unpackId(data?.id);
   const saveAs = React.useCallback(
      ({partnerName}) => {
         dispatch(
            partnersActions.saveAs(id, objectType, partnerName, partner, {
               onSuccess,
               onFailure: onClose,
            })
         );
      },
      [dispatch, id, objectType, partner, onSuccess, onClose]
   );

   const partnerName = React.useMemo(() => {
      partnersForValidation.current =
         data?.objectType === DiplomatObjectType.enum.EXTERNAL_PARTNER
            ? publicPartners
            : trustedPartners;
      return t('partner');
   }, [data, publicPartners, trustedPartners, t]);

   const validateName = (value: string) => {
      const isNameExists = partnersForValidation.current.some(
         (obj: IDiplomatObject) => obj.name === value
      );
      if (isNameExists) {
         return t('Diplomat already contains a {{partnerName}} named {{name}}', {
            partnerName,
            name: value,
         });
      }
   };

   return (
      <Dialog maxWidth="sm" header={t('Save Partner As')} close={onClose}>
         <Formik
            initialValues={validationSchema.cast({partnerName: data?.name})}
            // validationSchema={validationSchema}
            onSubmit={saveAs}
         >
            <Grid container spacing={2} justifyContent="center">
               <Grid item xs={8}>
                  <FormikTextField
                     autoFocus
                     type="text"
                     name="partnerName"
                     label={t('New {{partnerName}} Name:', {partnerName})}
                     required
                     validate={validateName}
                  />
               </Grid>
            </Grid>
            <Grid item xs={12}>
               <FormFooterButtons cancel={onClose} />
            </Grid>
         </Formik>
      </Dialog>
   );
};

export default SaveAsPartnerDialog;
